/**
 * Nickname generation, sanitization, and deterministic color assignment
 * for client-side chat protocol adapters.
 */

/**
 * Generates an ephemeral anonymous handle in the format Podcastie_XXXX (4 hex chars).
 */
export function generateAnonymousNick(): string {
  const hex = Math.floor(Math.random() * 0x10000)
    .toString(16)
    .padStart(4, '0');
  return `Podcastie_${hex}`;
}

/**
 * Sanitizes a raw nickname or display name to a valid IRC nick:
 * - Allowed characters: /^[a-zA-Z0-9_\-\[\]\\^{}|`]+$/
 * - Must not start with a digit (prepends 'p_' if starting with a digit)
 * - Max length 16 characters
 * - Falls back to generateAnonymousNick() if empty after stripping invalid chars
 */
export function sanitizeNick(raw: string): string {
  if (!raw) {
    return generateAnonymousNick();
  }

  // Replace invalid characters with underscore, collapse consecutive underscores, and trim them
  let cleaned = raw
    .trim()
    .replace(/[^a-zA-Z0-9_\-\[\]\\^{}|`]+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!cleaned) {
    return generateAnonymousNick();
  }

  // IRC nicks cannot start with a digit or hyphen
  if (/^[0-9\-]/.test(cleaned)) {
    cleaned = `p_${cleaned}`;
  }

  // Max 16 chars
  if (cleaned.length > 16) {
    cleaned = cleaned.slice(0, 16).replace(/_+$/, '');
  }

  return cleaned;
}

/**
 * Returns a deterministic HSL color string based on the string hash of the nick.
 * Format: `hsl(hue, 65%, 45%)` (or light/dark compatible vibrant hue).
 */
export function senderColor(nick: string): string {
  let hash = 0;
  for (let i = 0; i < nick.length; i++) {
    hash = (hash << 5) - hash + nick.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 45%)`;
}
